import { useSelector } from 'react-redux';
import { Calendar, UserPlus, ArrowRight } from 'lucide-react';
import AttachmentTile from './AttachmentTile';

/**
 * AttachMenuCalendarShareButtons - Component that determines which calendar buttons to show
 * based on chat participants and user role
 *
 * Logic:
 * - If 3 users in chat and user is IP: show both Request and Share buttons (split layout)
 * - If 2 users in chat and user is IP: show only Share button (full tile)
 * - If donor: always show only Request button (full tile)
 *
 * Props:
 * - chatId: The ID of the current chat
 * - onRequestClick: Handler for Request button click
 * - onShareClick: Handler for Share button click
 */
function AttachMenuCalendarShareButtons({ chatId, onRequestClick, onShareClick }) {
  const currentUser = useSelector((s) => s.user.profile);
  const currentUserRole = currentUser?.role;
  const currentUserId = currentUser?.id;
  
  // Get chat data from Redux
  const chat = useSelector((s) => s.chats.chats[chatId]);
  const chatUserIds = chat?.user_ids || [];
  
  // Determine which buttons to show
  const isGroupChat = chatUserIds.length >= 3;
  const isIP = currentUserRole === 'ip';
  const isDonor = currentUserRole === 'donor';
  
  // Donor: always show Request button
  if (isDonor) {
    return (
      <AttachmentTile
        icon={
          <div className="text-sky-500 flex items-center gap-1">
            <Calendar className="h-8 w-8" />
            <UserPlus className="h-8 w-8" />
          </div>
        }
        label="Request"
        onClick={onRequestClick}
      />
    );
  }
  
  // IP in group chat (3+ users): show both buttons in split layout
  if (isIP && isGroupChat) {
    return (
      <div className="flex flex-col gap-1 aspect-square">
        <button
          onClick={onRequestClick}
          className="flex flex-1 items-center justify-center gap-2 rounded-2xl border-2 border-sky-200 bg-card p-4 transition-colors hover:bg-accent hover:border-sky-300"
        >
          <div className="text-sky-500 flex items-center gap-1">
            <Calendar className="h-8 w-8" />
            <UserPlus className="h-8 w-8" />
          </div>
          <span className="text-sm font-medium text-foreground">Request</span>
        </button>
        <button
          onClick={onShareClick}
          className="flex flex-1 items-center justify-center gap-2 rounded-2xl border-2 border-sky-200 bg-card p-4 transition-colors hover:bg-accent hover:border-sky-300"
        >
          <div className="text-sky-500 flex items-center gap-1">
            <Calendar className="h-8 w-8" />
            <ArrowRight className="h-8 w-8" />
          </div>
          <span className="text-sm font-medium text-foreground">Share</span>
        </button>
      </div>
    );
  }
  
  // IP in 1-on-1 chat: show only Share button
  if (isIP) {
    return (
      <AttachmentTile
        icon={
          <div className="text-sky-500 flex items-center gap-1">
            <Calendar className="h-8 w-8" />
            <ArrowRight className="h-8 w-8" />
          </div>
        }
        label="Share"
        onClick={onShareClick}
      />
    );
  }
  
  // Fallback: show nothing if role is unknown
  return null;
}

export default AttachMenuCalendarShareButtons;
